import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { format, formatDistanceToNow } from 'date-fns';
import { 
  MessageSquare, 
  Phone, 
  Link2, 
  ArrowRight,
  Loader2,
  Inbox,
  CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { maskPhone } from '@/lib/maskPII';
import { InboxReplyComposer } from '@/components/inbox/InboxReplyComposer';
import { InboundRoutingFailures } from '@/components/inbox/InboundRoutingFailures';

interface InboxThread {
  id: string;
  shop_id: string;
  customer_phone: string;
  customer_id: string | null;
  status: string;
  first_message_at: string;
  last_message_at: string;
  message_count: number;
  attached_ticket_id: string | null;
  created_at: string;
}

interface InboxEvent {
  id: string;
  inbox_thread_id: string;
  event_type: string;
  event_source?: string;
  content: string;
  created_at: string;
}

export default function CustomerInbox() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;
  const queryClient = useQueryClient();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);

  // Fetch all inbox threads (unattached conversations)
  const threadsQuery = useQuery({
    queryKey: ['customer-inbox-threads', shopId],
    queryFn: async () => {
      if (!shopId) return [];
      
      // R125-H-8: narrow select — this table stores `customer_phone` in
      // plaintext (migration 20260130154936:47). The InboxThread interface
      // declares the 10 fields above; select('*') would also pull any
      // future internal columns (phone_bi is fine — already known) and
      // carry them unused into the React Query cache. The list matches
      // InboxThread exactly so consumers keep the same shape.
      const { data, error } = await supabase
        .from('customer_inbox_threads')
        .select('id, shop_id, customer_phone, customer_id, status, first_message_at, last_message_at, message_count, attached_ticket_id, created_at')
        .eq('shop_id', shopId)
        .is('attached_ticket_id', null)
        .order('last_message_at', { ascending: false });

      if (error) throw error;
      return data as InboxThread[];
    },
    enabled: !!shopId,
  });

  // Fetch events for selected thread
  // FIX-R58-LOW-1: explicit shop_id filter on all inbox queries/mutations.
  // RLS on these tables already scopes to the shop, but the explicit
  // predicate matches the R34/R56 defense-in-depth pattern: makes the
  // tenant boundary visible in code review and fails closed if RLS drifts.
  const eventsQuery = useQuery({
    queryKey: ['inbox-thread-events', shopId, selectedThreadId],
    queryFn: async () => {
      if (!selectedThreadId || !shopId) return [];

      // OCIS-R66-FIX (MED-7): secondary sort by id — see useTicketThread for
      // rationale (batched inserts can tie on created_at).
      // R125-M-5: narrow select — matches InboxEvent interface (line 36-42).
      const { data, error } = await supabase
        .from('ticket_thread_events')
        .select('id, inbox_thread_id, event_type, event_source, content, created_at')
        .eq('shop_id', shopId)
        .eq('inbox_thread_id', selectedThreadId)
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });

      if (error) throw error;
      return data as InboxEvent[];
    },
    enabled: !!selectedThreadId && !!shopId,
  });

  // Mark thread as archived (resolved)
  const resolveMutation = useMutation({
    mutationFn: async (threadId: string) => {
      if (!shopId) throw new Error('No active shop');
      const { error } = await supabase
        .from('customer_inbox_threads')
        .update({ status: 'archived' })
        .eq('shop_id', shopId)
        .eq('id', threadId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Thread archived');
      queryClient.invalidateQueries({ queryKey: ['customer-inbox-threads'] });
      setSelectedThreadId(null);
    },
    onError: () => {
      toast.error('Failed to archive thread');
    },
  });

  // Mark thread as read
  const markReadMutation = useMutation({
    mutationFn: async (threadId: string) => {
      if (!shopId) throw new Error('No active shop');
      const { error } = await supabase
        .from('customer_inbox_threads')
        .update({ status: 'read' })
        .eq('shop_id', shopId)
        .eq('id', threadId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-inbox-threads'] });
    },
  });

  // Auto-mark read when selecting thread
  const handleSelectThread = (threadId: string) => {
    setSelectedThreadId(threadId);
    const thread = threadsQuery.data?.find(t => t.id === threadId);
    if (thread?.status === 'unread') {
      markReadMutation.mutate(threadId);
    }
  };

  const selectedThread = threadsQuery.data?.find(t => t.id === selectedThreadId);
  const openThreads = threadsQuery.data?.filter(t => 
    t.status === 'unread' || t.status === 'read'
  ) || [];

  return (
    <div className="flex h-full">
      {/* Thread List */}
      <div className="w-80 border-r border-border flex flex-col">
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Customer Inbox</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {openThreads.length} unattached conversation{openThreads.length !== 1 ? 's' : ''}
          </p>
          <InboundRoutingFailures shopId={shopId} />
        </div>

        <ScrollArea className="flex-1">
          {threadsQuery.isLoading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : openThreads.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <CheckCircle2 className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">All caught up!</p>
              <p className="text-xs text-muted-foreground mt-1">
                No unattached messages
              </p>
            </div>
          ) : (
            <div className="p-2">
              {openThreads.map(thread => (
                <button
                  key={thread.id}
                  onClick={() => handleSelectThread(thread.id)}
                  className={cn(
                    'w-full text-left p-3 rounded-lg transition-colors',
                    selectedThreadId === thread.id
                      ? 'bg-primary/10 border border-primary/20'
                      : 'hover:bg-muted'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {thread.status === 'unread' && (
                        <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                      )}
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">
                        {maskPhone(thread.customer_phone)}
                      </span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {thread.message_count}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {formatDistanceToNow(new Date(thread.last_message_at), { addSuffix: true })}
                  </p>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Message View */}
      <div className="flex-1 flex flex-col">
        {!selectedThread ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">Select a conversation to view</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="font-semibold">{maskPhone(selectedThread.customer_phone)}</h2>
                <p className="text-xs text-muted-foreground">
                  {selectedThread.message_count} message{selectedThread.message_count !== 1 ? 's' : ''} • 
                  Started {format(new Date(selectedThread.first_message_at), 'MMM d, h:mm a')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled>
                  <Link2 className="h-4 w-4 mr-1.5" />
                  Attach to Ticket
                </Button>
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={() => resolveMutation.mutate(selectedThread.id)}
                  disabled={resolveMutation.isPending}
                >
                  {resolveMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-1.5" />
                      Resolve
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {eventsQuery.isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-16 w-3/4" />
                  ))}
                </div>
              ) : eventsQuery.data?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No messages in this thread
                </p>
              ) : (
                <div className="space-y-3">
                  {eventsQuery.data?.map(event => (
                    <div 
                      key={event.id}
                      className={cn(
                        'max-w-[75%] rounded-lg p-3',
                        event.event_type === 'sms_inbound'
                          ? 'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800'
                          : 'bg-muted ml-auto'
                      )}
                    >
                      <p className="text-sm whitespace-pre-wrap">{event.content}</p>
                      {/* CORE-AF: AI-supplied context is marked as derived, never as a person's words */}
                      {event.event_source === 'ai' && (
                        <p className="text-xs text-purple-700 mt-1" data-testid="inbox-derived">Derived by AI — not written by a person</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(event.created_at), 'h:mm a')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            {/* Core-100 CORE-BA: reply from the inbox (send-sms; consent, STOP and rate limit enforced server-side). */}
            {shopId && (
              <InboxReplyComposer
                shopId={shopId}
                customerId={selectedThread.customer_id ?? null}
                onSent={() => queryClient.invalidateQueries({ queryKey: ['inbox-thread-events', shopId, selectedThreadId] })}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
